import { Input } from "@/components/ui/input"
import { useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { UpcomingEventsTable } from "@/components/dashboard/UpcomingEventsTable"
import { Skeleton } from "@/components/ui/skeleton"
import { getUpcomingNPOEvents } from "@/lib/queries/events"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Search, Users, Inbox } from "lucide-react"
import { getNPOHosts } from "@/lib/queries/npo"
import { Button } from "@/components/ui/button"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Badge } from "@/components/ui/badge"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { useNavigate } from "react-router-dom"

export default function NPOEvents({ session, profile }: { session: any; profile: any }) {
  const [searchQuery, setSearchQuery] = useState("")
  const [currentPage, setCurrentPage] = useState(1)
  const navigate = useNavigate()

  const { data: response = { data: [], pagination: { page: 1, limit: 10, total: 0, totalPages: 1 } }, isLoading } = useQuery({
    queryKey: ["npo-events", profile?.id, currentPage],
    queryFn: () => getUpcomingNPOEvents(session?.access_token!, 10, currentPage),
    enabled: !!profile?.id && !!session?.access_token,
  })

  const events = response.data || []

  console.log('All NPO events:', events.map(e => ({ name: e.event_name, status: e.status })));

  const upcomingEvents = events.filter(
    (event) => {
      const isUpcoming = !["event_complete", "event_cancelled", "payment_failed"].includes(event.status);
      const matchesSearch = event.event_name.toLowerCase().includes(searchQuery.toLowerCase());
      return isUpcoming && matchesSearch;
    }
  )

  const completedEvents = events.filter(
    (event) => {
      const isCompleted = event.status === "event_complete";
      const matchesSearch = event.event_name.toLowerCase().includes(searchQuery.toLowerCase());
      return isCompleted && matchesSearch;
    }
  )

  const cancelledEvents = events.filter(
    (event) => {
      const isCancelled = ["event_cancelled", "payment_failed"].includes(event.status);
      const matchesSearch = event.event_name.toLowerCase().includes(searchQuery.toLowerCase());
      return isCancelled && matchesSearch;
    }
  )

  const { data: hosts = [], isLoading: hostsLoading } = useQuery({
    queryKey: ["npo-hosts", profile?.id],
    queryFn: () => getNPOHosts(profile?.id),
    enabled: !!profile?.id,
  })

  const handleHostRowClick = (hostId: string) => {
    navigate(`/dashboard/hosts/${hostId}`)
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Events</h1>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            className="flex items-center gap-2"
            onClick={() => navigate("/dashboard/events/invites")}
          >
            <Inbox className="h-4 w-4" />
            Pending Invites
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="flex items-center gap-2"
            onClick={() => navigate("/dashboard/events/hosts")}
          >
            <Users className="h-4 w-4" />
            Hosts
          </Button>
          <Button
            className="bg-green-600 hover:bg-green-700 text-white font-bold shadow-lg flex items-center gap-2"
            size="sm"
            onClick={() => navigate('/dashboard/corporate-events/rfqs')}
          >
            <span role="img" aria-label="rfq">📄</span> RFQ Requests
          </Button>
        </div>
      </div>

      <div className="flex items-center space-x-2">
        <Search className="w-4 h-4 text-gray-500" />
        <Input
          placeholder="Search events..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="max-w-sm"
        />
      </div>

      <Tabs defaultValue="upcoming" className="space-y-4">
        <TabsList>
          <TabsTrigger value="upcoming">
            Upcoming Events {upcomingEvents.length > 0 && `(${upcomingEvents.length})`}
          </TabsTrigger>
          <TabsTrigger value="completed">
            Completed Events {completedEvents.length > 0 && `(${completedEvents.length})`}
          </TabsTrigger>
          <TabsTrigger value="cancelled">
            Cancelled Events {cancelledEvents.length > 0 && `(${cancelledEvents.length})`}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="upcoming" className="space-y-4">
          {isLoading ? (
            <div className="space-y-4">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : upcomingEvents.length > 0 ? (
            <UpcomingEventsTable 
              events={upcomingEvents} 
              session={session} 
              profile={profile} 
              pagination={response.pagination}
              onPageChange={(page) => setCurrentPage(page)}
            />
          ) : (
            <div className="text-center text-gray-500 mt-8 p-8 border rounded-lg bg-background">
              <h3 className="text-lg font-medium mb-2">No Upcoming Events Found</h3>
              <p>
                {searchQuery
                  ? "No events match your search criteria. Try adjusting your search terms."
                  : "You don't have any upcoming events at the moment."}
              </p>
            </div>
          )}
        </TabsContent>

        <TabsContent value="completed" className="space-y-4">
          {isLoading ? (
            <div className="space-y-4">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : completedEvents.length > 0 ? (
            <UpcomingEventsTable 
              events={completedEvents} 
              session={session} 
              profile={profile} 
              pagination={response.pagination}
              onPageChange={(page) => setCurrentPage(page)}
            />
          ) : (
            <div className="text-center text-gray-500 mt-8 p-8 border rounded-lg bg-background">
              <h3 className="text-lg font-medium mb-2">No Completed Events Found</h3>
              <p>
                {searchQuery
                  ? "No events match your search criteria. Try adjusting your search terms."
                  : "You don't have any completed events at the moment."}
              </p>
            </div>
          )}
        </TabsContent>

        <TabsContent value="cancelled" className="space-y-4">
          {isLoading ? (
            <div className="space-y-4">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : cancelledEvents.length > 0 ? (
            <UpcomingEventsTable 
              events={cancelledEvents} 
              session={session} 
              profile={profile} 
              pagination={response.pagination}
              onPageChange={(page) => setCurrentPage(page)}
            />
          ) : (
            <div className="text-center text-gray-500 mt-8 p-8 border rounded-lg bg-background">
              <h3 className="text-lg font-medium mb-2">No Cancelled Events Found</h3>
              <p>
                {searchQuery
                  ? "No events match your search criteria. Try adjusting your search terms."
                  : "You don't have any cancelled events at the moment."}
              </p>
            </div>
          )}
        </TabsContent>

        <TabsContent value="hosts" className="space-y-4">
          {hostsLoading ? (
            <div className="space-y-4">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : (
            <div className="border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Host</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Events</TableHead>
                    <TableHead>Latest Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {hosts.map((host) => (
                    <TableRow 
                      key={host.id} 
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => handleHostRowClick(host.id)}
                    >
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Avatar>
                            <AvatarFallback>
                              {host.full_name.split(" ").map(n => n[0]).join("")}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <div className="font-medium">{host.full_name}</div>
                            <div className="text-sm text-muted-foreground">
                              {host.email}
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>{host.phone}</TableCell>
                      <TableCell>{host.events_count} events</TableCell>
                      <TableCell>
                        <Badge variant="secondary">
                          {host.latest_event_status.replace(/_/g, " ")}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  )
}